<template>
  <AccountContextWrapper>
    <template #default>
      <section class="budget-template-create mt-3">
        <div class="text-start mb-3">
          <h2 class="h5 mb-2">Budget Templates</h2>
          <p class="mb-0 text-muted">
            Create and manage reusable budget templates that can seed future
            budget periods.
          </p>
        </div>

        <bPanel
          title="Create Budget Template"
          tag="article"
          class="border"
          max-width="48rem"
        >
          <BForm @submit="onSubmit">
            <BFormGroup
              label="Template Name"
              label-for="template-name"
              class="mb-3 floating-group"
            >
              <BFormInput
                id="template-name"
                v-model="form.name"
                placeholder="Enter template name"
                required
              />
            </BFormGroup>

            <BFormGroup
              label="Add Category"
              label-for="category-selector"
              class="mb-2 floating-group"
            >
              <div class="d-flex gap-2">
                <BFormSelect
                  id="category-selector"
                  v-model="selectedCategoryUid"
                  :options="availableCategoryOptions"
                  :disabled="
                    loadingCategories || availableCategoryOptions.length <= 1
                  "
                />
                <BButton
                  type="button"
                  variant="outline-secondary"
                  :disabled="!selectedCategoryUid"
                  @click="addCategory"
                >
                  Add
                </BButton>
              </div>
            </BFormGroup>

            <div v-if="loadingCategories" class="text-center my-4">
              <BSpinner label="Loading categories..." />
            </div>

            <div
              v-else-if="form.categories.length === 0"
              class="text-center my-4"
            >
              <IconAlertTriangle class="me-1 text-warning" />
              Add at least one category before submitting.
            </div>

            <div v-else class="mb-3">
              <div
                v-for="(category, index) in form.categories"
                :key="category.categoryUid"
                class="border rounded p-2 mb-2"
              >
                <div class="row g-2 align-items-end">
                  <div class="col-md-6">
                    <BFormGroup
                      :label="`Category ${index + 1}`"
                      class="mb-0 floating-group"
                    >
                      <BFormInput :model-value="category.name" readonly />
                    </BFormGroup>
                  </div>
                  <div class="col-md-4">
                    <BFormGroup
                      label="Budget Amount"
                      class="mb-0 floating-group"
                    >
                      <BFormInput
                        :model-value="category.budgetAmount"
                        type="number"
                        min="0"
                        step="0.01"
                        required
                        @update:modelValue="
                          updateCategoryAmount(category.categoryUid, $event)
                        "
                      />
                    </BFormGroup>
                  </div>
                  <div class="col-md-2">
                    <BButton
                      type="button"
                      variant="outline-danger"
                      class="w-100"
                      @click="removeCategory(category.categoryUid)"
                    >
                      Remove
                    </BButton>
                  </div>
                </div>
              </div>
            </div>

            <div v-if="errorMessage" class="alert alert-danger">
              {{ errorMessage }}
            </div>

            <div class="d-flex justify-content-end gap-2">
              <BButton
                type="button"
                variant="outline-secondary"
                @click="goBack"
              >
                Cancel
              </BButton>
              <BButton type="submit" variant="primary" :disabled="submitting">
                {{ submitting ? "Creating..." : "Create Template" }}
              </BButton>
            </div>
          </BForm>
        </bPanel>
      </section>
    </template>
  </AccountContextWrapper>
</template>

<script>
import {
  BButton,
  BForm,
  BFormGroup,
  BFormInput,
  BFormSelect,
  BSpinner,
} from "bootstrap-vue-next";
import { IconAlertTriangle } from "@tabler/icons-vue";
import bPanel from "@/components/atoms/bPanel.vue";
import AccountContextWrapper from "@/components/molecules/AccountContextWrapper.vue";
import {
  budgetTemplates as budgetTemplatesNs,
  spendingCategory as spendingCategoryNs,
  userAccount as userAcctNs,
  userSession as userSessNs,
} from "@/store/namespaces";

export default {
  name: "BudgetTemplatesCreateView",
  components: {
    AccountContextWrapper,
    BButton,
    BForm,
    BFormGroup,
    BFormInput,
    BFormSelect,
    BSpinner,
    IconAlertTriangle,
    bPanel,
  },
  data() {
    return {
      loadingCategories: false,
      submitting: false,
      errorMessage: "",
      selectedCategoryUid: null,
      form: {
        name: "",
        categories: [],
      },
    };
  },
  computed: {
    acctUid() {
      return this.$store.getters[`${userAcctNs}/getSelectedAcctUid`];
    },
    spendingCategories() {
      return (
        this.$store.getters[`${spendingCategoryNs}/getSpendingCategories`] || []
      );
    },
    availableCategoryOptions() {
      const pickedCategoryUids = new Set(
        this.form.categories.map((category) => category.categoryUid)
      );

      const options = this.spendingCategories
        .filter((category) => !pickedCategoryUids.has(category.item.uid))
        .map((category) => ({
          value: category.item.uid,
          text: category.item.name,
        }));

      return [{ value: null, text: "Select a category" }, ...options];
    },
  },
  async created() {
    await this.fetchCategories();
  },
  methods: {
    async fetchCategories() {
      try {
        this.loadingCategories = true;
        this.errorMessage = "";
        // This method already handles caching internally, so we can call it without checking if we already have categories
        const params = { sort: "name:asc" };
        await this.$store.dispatch(
          `${spendingCategoryNs}/fetchSpendingCategories`,
          params
        );
      } catch (error) {
        console.error("Error fetching categories:", error);
        this.errorMessage = "Unable to load spending categories.";
      } finally {
        this.loadingCategories = false;
      }
    },
    addCategory() {
      if (!this.selectedCategoryUid) {
        return;
      }

      const selected = this.spendingCategories.find(
        (category) => category.item.uid === this.selectedCategoryUid
      )?.item;

      if (!selected) {
        return;
      }

      this.form.categories = [
        ...this.form.categories,
        {
          categoryUid: selected.uid,
          name: selected.name,
          budgetAmount: "",
        },
      ];
      this.selectedCategoryUid = null;
    },
    removeCategory(categoryUid) {
      this.form.categories = this.form.categories.filter(
        (category) => category.categoryUid !== categoryUid
      );
    },
    updateCategoryAmount(categoryUid, amount) {
      this.form.categories = this.form.categories.map((category) => {
        if (category.categoryUid !== categoryUid) {
          return category;
        }

        return {
          ...category,
          budgetAmount: amount,
        };
      });
    },
    getValidatedPayload() {
      const name = this.form.name.trim();
      if (!name) {
        return { error: "Template name is required." };
      }

      if (this.form.categories.length === 0) {
        return { error: "At least one category is required." };
      }

      const categories = this.form.categories.map((category) => ({
        categoryUid: category.categoryUid,
        name: category.name,
        budgetAmount: Number(category.budgetAmount),
      }));

      const hasInvalidAmounts = categories.some(
        (category) =>
          Number.isNaN(category.budgetAmount) || category.budgetAmount < 0
      );
      if (hasInvalidAmounts) {
        return {
          error:
            "Each category must have a budget amount greater than or equal to zero.",
        };
      }

      return {
        payload: {
          name,
          categories,
        },
      };
    },
    async onSubmit(event) {
      event.preventDefault();
      this.errorMessage = "";

      const validation = this.getValidatedPayload();
      if (validation.error) {
        this.errorMessage = validation.error;
        return;
      }

      try {
        this.submitting = true;
        const userInfo = await this.$store.dispatch(
          `${userSessNs}/getUserInfo`
        );
        await this.$store.dispatch(
          `${budgetTemplatesNs}/createBudgetTemplate`,
          {
            acctUid: this.acctUid,
            userId: userInfo?.id,
            ...validation.payload,
          }
        );
        this.$notify.notifySuccess("Budget template created successfully.");
        this.$router.push({ path: "/budget/templates" });
      } catch (error) {
        console.error("Error creating budget template:", error);
        this.errorMessage =
          "Unable to create budget template. Please try again.";
      } finally {
        this.submitting = false;
      }
    },
    goBack() {
      this.$router.push({ path: "/budget/templates" });
    },
  },
};
</script>

<style scoped>
.budget-template-create {
  width: min(100%, 48rem);
}
</style>
